// Compresión de fotos en el navegador antes de subirlas al chat. Una foto del celular pesa varios MB y casi nunca hace falta
// tanto para verla en una burbuja: se reduce a un máximo de LADO_MAX píxeles y se vuelve a guardar con menos calidad.
// Así se sube mucho menos (más rápido y sin gastar el plan de Cloudinary) y, de paso, se pierden los datos ocultos de la foto
// (ubicación, modelo del teléfono) porque se vuelve a dibujar.
// Nunca estorba: si algo falla (formato raro, navegador sin canvas…) se envía el archivo original.

export const LADO_MAX = 1600;            // píxeles del lado más largo
export const CALIDAD = 0.82;
export const MIN_BYTES = 300 * 1024;     // las fotos más ligeras que esto no vale la pena tocarlas

const TIPOS = ['image/jpeg', 'image/png', 'image/webp'];
const EXTENSION = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

// Abre la imagen y devuelve { ancho, alto, dibujar(ctx, w, h), liberar() }. createImageBitmap respeta la orientación de la foto
// (EXIF) y no bloquea la pantalla; en navegadores sin él se usa una <img>.
const abrirImagen = async (archivo) => {
  if (typeof createImageBitmap === 'function') {
    let bitmap;
    try {
      bitmap = await createImageBitmap(archivo, { imageOrientation: 'from-image' });
    } catch (err) {
      bitmap = await createImageBitmap(archivo);   // navegadores que no aceptan opciones
    }
    return {
      ancho: bitmap.width, alto: bitmap.height,
      dibujar: (ctx, w, h) => ctx.drawImage(bitmap, 0, 0, w, h),
      liberar: () => { if (typeof bitmap.close === 'function') bitmap.close(); },
    };
  }
  const url = URL.createObjectURL(archivo);
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('No se pudo leer la imagen'));
    i.src = url;
  });
  return {
    ancho: img.naturalWidth, alto: img.naturalHeight,
    dibujar: (ctx, w, h) => ctx.drawImage(img, 0, 0, w, h),
    liberar: () => URL.revokeObjectURL(url),
  };
};

const aBlob = (canvas, tipo, calidad) => new Promise((resolve) => {
  try { canvas.toBlob((b) => resolve(b), tipo, calidad); } catch (err) { resolve(null); }
});

// ¿La imagen tiene partes transparentes? Se revisa una copia chica (64 px): basta para notar un fondo transparente.
const tieneTransparencia = (imagen) => {
  const lado = 64;
  const c = document.createElement('canvas');
  c.width = lado; c.height = lado;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  imagen.dibujar(ctx, lado, lado);
  const datos = ctx.getImageData(0, 0, lado, lado).data;
  for (let i = 3; i < datos.length; i += 4) if (datos[i] < 255) return true;
  return false;
};

// Nombre con la extensión del tipo nuevo ("captura.PNG" → "captura.jpg")
const nombreConTipo = (nombre, tipo) => {
  const base = nombre.includes('.') ? nombre.slice(0, nombre.lastIndexOf('.')) : nombre;
  return `${base}.${EXTENSION[tipo]}`;
};

// Devuelve un File más ligero, o el mismo archivo si no es una foto que valga la pena comprimir, si no se logró algo más
// chico o si algo falló.
export const comprimirImagen = async (archivo) => {
  let imagen = null;
  try {
    if (!archivo || !TIPOS.includes(archivo.type) || archivo.size < MIN_BYTES) return archivo;
    imagen = await abrirImagen(archivo);
    if (!imagen.ancho || !imagen.alto) return archivo;

    const escala = Math.min(1, LADO_MAX / Math.max(imagen.ancho, imagen.alto));
    const w = Math.max(1, Math.round(imagen.ancho * escala));
    const h = Math.max(1, Math.round(imagen.alto * escala));

    // Un PNG sin transparencia pesa mucho menos como JPEG; con transparencia se queda PNG para no pintarle fondo negro
    const conTransparencia = archivo.type === 'image/png' && tieneTransparencia(imagen);
    const tipo = archivo.type === 'image/png' && !conTransparencia ? 'image/jpeg' : archivo.type;

    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (tipo === 'image/jpeg') { ctx.fillStyle = 'white'; ctx.fillRect(0, 0, w, h); }   // JPEG no tiene transparencia
    imagen.dibujar(ctx, w, h);

    const blob = await aBlob(canvas, tipo, CALIDAD);
    // Si el navegador no sabe guardar ese formato devuelve otro (p. ej. PNG en vez de WebP): no sirve; tampoco si no pesa menos
    if (!blob || blob.type !== tipo || blob.size >= archivo.size) return archivo;
    return new File([blob], nombreConTipo(archivo.name, tipo), { type: tipo, lastModified: archivo.lastModified });
  } catch (err) {
    return archivo;
  } finally {
    if (imagen) imagen.liberar();
  }
};
